/**
 * RichTextEditor Component
 *
 * Google Docs-style WYSIWYG editor built on TipTap (ProseMirror).
 * Accepts and outputs Markdown via tiptap-markdown extension.
 *
 * TOOLBAR:
 * - Paragraph type dropdown (Normal, Heading 1, Heading 2)
 * - Bold, Italic, Underline buttons
 * - Bullet list button
 * - Strikethrough button
 *
 * DATA FLOW:
 * Markdown (slide.content) → TipTap loads → user edits WYSIWYG → TipTap exports markdown → onChange
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Markdown } from 'tiptap-markdown';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Minus } from 'lucide-react';
import { cn } from '../lib/utils';

interface RichTextEditorProps {
  content: string;          // Markdown content
  onChange: (markdown: string) => void;
  placeholder?: string;
  // Font controls (optional — shown in toolbar when provided)
  fontFamily?: string;
  onFontFamilyChange?: (value: string) => void;
  fontScale?: number;
  onFontScaleChange?: (value: number) => void;
}

const FONT_KEYS: Record<string, string> = {
  GLOBAL: 'fontGlobal',
  MODERN: 'fontModern',
  SERIF: 'fontSerif',
  TECH: 'fontTech',
  ELEGANT: 'fontElegant',
  BOLD: 'fontModern',
  MINIMAL: 'fontMinimal',
  HANDWRITTEN: 'fontHandwritten',
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder,
  fontFamily,
  onFontFamilyChange,
  fontScale,
  onFontScaleChange,
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('editor.richText.placeholder');

  // Track current heading level reactively (must be before useEditor)
  const [currentHeadingLevel, setCurrentHeadingLevel] = useState<'paragraph' | 'h1' | 'h2'>('paragraph');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2],
        },
        bulletList: {},
        orderedList: {},
        bold: {},
        italic: {},
        strike: {},
        // Disable code block, blockquote, etc. - not needed for carousels
        codeBlock: false,
        blockquote: false,
        code: false,
        horizontalRule: false,
      }),
      Underline,
      Markdown.configure({
        html: true,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: content,
    onUpdate: ({ editor }) => {
      // Force html mode on storage (bypasses Vite HMR caching old useEditor config)
      if (editor.storage.markdown) {
        (editor.storage.markdown as any).options.html = true;
      }
      const markdown = (editor.storage.markdown as any).getMarkdown();
      onChange(markdown);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[8rem] px-3 py-2 text-foreground',
      },
    },
  });

  // Sync external content changes (e.g., AI refinement, slide switch)
  useEffect(() => {
    if (editor && content !== (editor.storage.markdown as any).getMarkdown()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // Update heading level state when cursor moves or content changes
  useEffect(() => {
    if (!editor) return;

    const updateHeadingLevel = () => {
      if (editor.isActive('heading', { level: 1 })) {
        setCurrentHeadingLevel('h1');
      } else if (editor.isActive('heading', { level: 2 })) {
        setCurrentHeadingLevel('h2');
      } else {
        setCurrentHeadingLevel('paragraph');
      }
    };

    // Update immediately
    updateHeadingLevel();

    // Listen for selection and content changes
    editor.on('selectionUpdate', updateHeadingLevel);
    editor.on('update', updateHeadingLevel);

    return () => {
      editor.off('selectionUpdate', updateHeadingLevel);
      editor.off('update', updateHeadingLevel);
    };
  }, [editor]);

  const setHeadingLevel = useCallback((value: string) => {
    if (!editor) return;
    if (value === 'h1') {
      editor.chain().focus().toggleHeading({ level: 1 }).run();
    } else if (value === 'h2') {
      editor.chain().focus().toggleHeading({ level: 2 }).run();
    } else {
      editor.chain().focus().setParagraph().run();
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="mb-3 border border-border rounded-lg overflow-hidden bg-background">
      {/* Toolbar */}
      <div className="px-2 py-2 bg-secondary/50 border-b border-border space-y-2">
        {/* Row 1: single block with Font + Type + List */}
        <div className="rounded-lg border border-border bg-background/70 px-2 py-1.5">
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-2">
            {onFontFamilyChange && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  {t('editor.richText.fontLabel')}
                </p>
                <Select
                  value={fontFamily || 'GLOBAL'}
                  onValueChange={(v) => onFontFamilyChange(v)}
                >
                  <SelectTrigger className="h-8 w-full min-w-0 text-xs bg-background px-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(FONT_KEYS).map(([value, key]) => (
                      <SelectItem key={value} value={value}>{t(`editor.richText.${key}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                {t('editor.richText.typeLabel')}
              </p>
              <Select
                value={currentHeadingLevel}
                onValueChange={setHeadingLevel}
              >
                <SelectTrigger className="h-8 w-full min-w-0 text-xs bg-background px-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paragraph">{t('editor.richText.normal')}</SelectItem>
                  <SelectItem value="h1">{t('editor.richText.heading1')}</SelectItem>
                  <SelectItem value="h2">{t('editor.richText.heading2')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                {t('editor.richText.listLabel')}
              </p>
              <div className="flex items-center rounded-md border border-border overflow-hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={cn(
                    "h-8 w-8 p-0 rounded-none border-r border-border text-sm",
                    editor.isActive('bulletList') ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'
                  )}
                  title={t('editor.richText.bulletList')}
                  aria-label={t('editor.richText.bulletList')}
                >
                  •
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={cn(
                    "h-8 w-8 p-0 rounded-none text-xs",
                    editor.isActive('orderedList') ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'
                  )}
                  title={t('editor.richText.numberedList')}
                  aria-label={t('editor.richText.numberedList')}
                >
                  1.
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Size -> Style */}
        <div className="rounded-lg border border-border bg-background/70 px-2 py-1.5">
          <div className={cn("grid items-end gap-2", onFontScaleChange ? "grid-cols-[auto_1fr]" : "grid-cols-1")}>
            {onFontScaleChange && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  {t('editor.richText.sizeLabel')}
                </p>
                <div className="flex items-center rounded-md border border-border overflow-hidden">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 rounded-none border-r border-border"
                    onClick={() => {
                      const currentPercent = (fontScale ?? 1.0) * 100;
                      const newPercent = Math.max(50, currentPercent - 5);
                      onFontScaleChange(newPercent / 100);
                    }}
                    disabled={(fontScale ?? 1.0) <= 0.5}
                    title={t('editor.richText.decreaseFontSize')}
                    aria-label={t('editor.richText.decreaseFontSize')}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>

                  <div className="flex items-center px-2">
                    <Input
                      type="number"
                      min={50}
                      max={200}
                      step={5}
                      value={Math.round((fontScale ?? 1.0) * 100)}
                      onChange={(e) => {
                        const percent = Number(e.target.value);
                        if (percent >= 50 && percent <= 200) {
                          onFontScaleChange(percent / 100);
                        }
                      }}
                      className="h-8 w-14 text-xs text-center border-0 p-0 focus-visible:ring-0"
                      aria-label={t('editor.richText.sizeLabel')}
                    />
                    <span className="text-xs text-muted-foreground ml-1">%</span>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 rounded-none border-l border-border"
                    onClick={() => {
                      const currentPercent = (fontScale ?? 1.0) * 100;
                      const newPercent = Math.min(200, currentPercent + 5);
                      onFontScaleChange(newPercent / 100);
                    }}
                    disabled={(fontScale ?? 1.0) >= 2.0}
                    title={t('editor.richText.increaseFontSize')}
                    aria-label={t('editor.richText.increaseFontSize')}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                {t('editor.richText.styleLabel')}
              </p>
              <div className="flex items-center rounded-md border border-border overflow-hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={cn(
                    "h-8 w-8 p-0 rounded-none border-r border-border text-xs font-bold",
                    editor.isActive('bold') ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'
                  )}
                  title={t('editor.richText.bold')}
                  aria-label={t('editor.richText.bold')}
                >
                  B
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={cn(
                    "h-8 w-8 p-0 rounded-none border-r border-border text-xs italic",
                    editor.isActive('italic') ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'
                  )}
                  title={t('editor.richText.italic')}
                  aria-label={t('editor.richText.italic')}
                >
                  I
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  className={cn(
                    "h-8 w-8 p-0 rounded-none border-r border-border text-xs underline",
                    editor.isActive('underline') ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'
                  )}
                  title={t('editor.richText.underline')}
                  aria-label={t('editor.richText.underline')}
                >
                  U
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  className={cn(
                    "h-8 w-8 p-0 rounded-none text-xs line-through",
                    editor.isActive('strike') ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'
                  )}
                  title={t('editor.richText.strikethrough')}
                  aria-label={t('editor.richText.strikethrough')}
                >
                  S
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
